import type { ImageProps } from "next/legacy/image";
import NextImage from "next/legacy/image";

const isValidUrl = (url) =>
  ["https://dwlq5braponyn.cloudfront.net/"].some((vu) => url.startsWith(vu));

const universalBtoa = (payload) => {
  if (typeof btoa !== "undefined") {
    return btoa(payload);
  }

  return Buffer.from(payload, "utf8").toString("base64");
};

const imageLoader =
  (preResizeEdits) =>
  ({ src, width }) => {
    if (!isValidUrl(src)) {
      return src;
    }

    if (src.endsWith(".svg")) {
      return src;
    }

    return imageUrl({ src, width, preResizeEdits });
  };

type Edits = {
  extract?: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
};

interface ImageUrlCommon {
  src: string;
  fit?: string;
  focalPoint?: {
    x: number;
    y: number;
  };
  preResizeEdits?: Edits;
  width?: number;
  height?: number;
}

interface ImageUrlWithWidth extends ImageUrlCommon {
  width: number;
}

interface ImageUrlWithHeight extends ImageUrlCommon {
  height: number;
}

interface ImageUrlWithEdits extends ImageUrlCommon {
  edits: Edits;
}

type ImageUrl =
  | ImageUrlWithWidth
  | ImageUrlWithHeight
  | (ImageUrlWithWidth & ImageUrlWithHeight)
  | ImageUrlWithEdits;

export const imageUrl = ({
  src,
  preResizeEdits = null,
  width = null,
  height = null,
  fit = "inside",
  focalPoint = null,
}: ImageUrl) => {
  if (!isValidUrl(src)) {
    throw new Error(
      "The image src URL must begin with the Cloufront URL associated with the S3 bucket for this image processor."
    );
  }

  const edits = (preResizeEdits ? { ...preResizeEdits } : {}) as any;

  edits.resize = {
    width,
    height,
    options: {
      fit,
    },
  };

  if (focalPoint) {
    // TODO calc coords for extracting the crop
    // edits.extract = {
    //   left,
    //   top,
    //   right,
    //   bottom,
    // }
  }

  const imageRequest = JSON.stringify({
    bucket: "finance-uncovered",
    key: src.replace("https://dwlq5braponyn.cloudfront.net/", "cms/"),
    edits,
  });

  const encodedRequest = universalBtoa(imageRequest);
  return `https://d3qb927aypgxv4.cloudfront.net/${encodedRequest}`;
};

type ImageWithLoaderProps = ImageProps & {
  preResizeEdits?: {
    [key: string]: any;
  };
};

function Image({ preResizeEdits = null, ...props }: ImageWithLoaderProps) {
  const src = props.src as string;

  return (
    <NextImage
      loader={imageLoader(preResizeEdits)}
      sizes="100vw"
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      alt={props.alt || ""}
      unoptimized={src.endsWith(".svg")}
    />
  );
}

export default Image;
